import { useState, useEffect, KeyboardEvent } from "react";
import {
  Button,
  Stack,
  H5,
  Message,
  Text,
  Checkbox
} from "@adfenix/alexandria";
import Map, {
  Marker,
  NavigationControl,
  MarkerDragEvent,
  FullscreenControl,
  GeolocateControl
} from "react-map-gl";
import { CampaignService } from "@adfenix/types";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import Select from "react-select";
import { toast } from "react-toastify";
import { handleError } from "api/helper";
import { useDebounceCallback } from "hooks/useDebounce";
import { useForm } from "react-hook-form";

interface Props {
  customCoordinates: CampaignService.Coordinates | undefined;
  setCustomCoordinates: (content: {
    longitude: number;
    latitude: number;
  }) => void;
  initialCoordinates?: CampaignService.Coordinates;
  hqCountry: string;
}

interface IData {
  lat: string;
  lon: string;
  display_name: string;
}

interface SelectOption {
  value: { lat: string | number; lon: string | number };
  label: string;
}

export const SearchLocation = ({
  customCoordinates,
  setCustomCoordinates,
  initialCoordinates,
  hqCountry
}: Props) => {
  const { register, watch } = useForm({
    mode: "onChange",
    defaultValues: {
      searchWorldwide: false
    }
  });
  const searchArea = hqCountry === "US" ? "us" : "eu"; //only for forward geocoding

  const defaultCoordinates =
    customCoordinates === undefined
      ? initialCoordinates ?? { longitude: 0, latitude: 0 }
      : customCoordinates;

  const [location, setLocation] = useState({
    lat: defaultCoordinates.latitude,
    lon: defaultCoordinates.longitude
  });

  const [viewport, setViewport] = useState({
    longitude: location.lon,
    latitude: location.lat,
    zoom: 16,
    pitch: 0,
    bearing: 0,
    padding: { top: 50, bottom: 50, left: 50, right: 50 },
    width: 600,
    height: 400
  });

  const searchWorldwide = watch("searchWorldwide");

  const updateLocationAndCoordinatesAndViewport = ({
    latitude,
    longitude
  }: CampaignService.Coordinates) => {
    setLocation({
      lat: latitude,
      lon: longitude
    });

    setViewport({
      ...viewport,
      latitude,
      longitude
    });

    setCustomCoordinates({
      latitude,
      longitude
    });
  };

  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const defaultSearchLocation = (searchString: string) => {
    return fetch(
      `https://${searchArea}1.locationiq.com/v1/search?key=${process.env.REACT_APP_LOCATION_IQ}&q=${searchString}&format=json`
    )
      .then(response => response.json())
      .catch(handleError);
  };

  const handleLocationSearchError = () => {
    setError("Cannot find a valid address");
    throw Error;
  };

  const handleSearchLocation = (searchString: string) => {
    try {
      setIsLoading(true);
      defaultSearchLocation(searchString)
        .then((data: { lat: number; lon: number; display_name: string }[]) => {
          setIsLoading(false);
          data.hasOwnProperty("error")
            ? handleLocationSearchError()
            : updateLocationAndCoordinatesAndViewport({
                latitude: data[0].lat,
                longitude: data[0].lon
              });
        })
        .then(() => setError(""));
    } catch {
      toast.error("Something went wrong with setting location");
    }
  };

  const resetDefaultCoordinates = () => {
    updateLocationAndCoordinatesAndViewport(
      initialCoordinates ?? { longitude: 0, latitude: 0 }
    );
    setError("");
  };

  const handleAutocompleteSearch = (
    searchString: string,
    worldwideSearch: boolean
  ) => {
    setError("");

    try {
      fetch(
        worldwideSearch
          ? `https://api.locationiq.com/v1/autocomplete?key=${process.env.REACT_APP_LOCATION_IQ}&q=${searchString}&limit=5`
          : `https://api.locationiq.com/v1/autocomplete?key=${
              process.env.REACT_APP_LOCATION_IQ
            }&q=${searchString}&limit=5&countrycodes=${hqCountry.toLowerCase()}
  `
      )
        .then(res => res.json())

        .then((data: IData[]) => {
          if (!Array.isArray(data)) {
            return;
          }

          const searchResults = data.map(d => {
            return {
              ...d,
              value: { lat: d.lat, lon: d.lon },
              label: d.display_name
            };
          });
          setOptions(searchResults);
        });
    } catch {
      toast.error("Something went wrong with setting location");
    }
  };

  const suggestions = useDebounceCallback(handleAutocompleteSearch, 300);

  useEffect(() => {
    if (search.length > 2) suggestions(search, searchWorldwide);
  }, [search, suggestions, searchWorldwide]);

  const handleMarkerDrag = (e: MarkerDragEvent) => {
    updateLocationAndCoordinatesAndViewport({
      latitude: e.lngLat.lat,
      longitude: e.lngLat.lng
    });
    setError("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.code === "Enter") {
      handleSearchLocation(search);
    }
  };

  return (
    <form>
      <Stack spacing={"s"}>
        <H5>Set location</H5>
        <Text as="p">
          Pick a location for your ad. We’ll set a radius automatically to
          display your ad to the most relevant audience with the largest reach
          in that area.
        </Text>
        <Text as="p" size="s">
          By default search results are limited to your specific country. To
          search worldwide please click checkbox below on this page.
        </Text>
        <Checkbox
          {...register("searchWorldwide")}
          label={"Search worldwide"}
          checked={searchWorldwide}
        />
        <Button
          variant="text"
          onClick={resetDefaultCoordinates}
          logLabel="reset-coordinates-button"
        >
          Reset to latest saved location
        </Button>

        {error && <Message type="error">{error}</Message>}

        <Map
          mapLib={maplibregl}
          mapStyle={`https://tiles.locationiq.com/v3/light/vector.json?key=${process.env.REACT_APP_LOCATION_IQ}`}
          viewState={viewport}
          initialViewState={{
            longitude: defaultCoordinates.longitude,
            latitude: defaultCoordinates.latitude,
            zoom: 16,
            pitch: 0,
            bearing: 0
          }}
          onDragEnd={e =>
            setViewport({
              ...viewport,
              latitude: e.viewState.latitude,
              longitude: e.viewState.longitude
            })
          }
          onZoomEnd={e =>
            setViewport({
              ...viewport,
              zoom: e.viewState.zoom
            })
          }
          style={{ maxWidth: 600, height: 400 }}
        >
          <GeolocateControl
            onGeolocate={e =>
              setViewport({
                ...viewport,
                latitude: e.coords.latitude,
                longitude: e.coords.longitude
              })
            }
            position="bottom-right"
          />
          <NavigationControl position="bottom-right" />
          <FullscreenControl position="bottom-right" />
          <Select
            isLoading={isLoading}
            onKeyDown={handleKeyDown}
            onInputChange={setSearch}
            isClearable
            placeholder={"Search"}
            onChange={e =>
              updateLocationAndCoordinatesAndViewport({
                //@ts-ignore
                latitude: e?.value.lat ?? location.lat,
                //@ts-ignore
                longitude: e?.value.lon ?? location.lon
              })
            }
            onMenuClose={() => setOptions([])}
            options={options}
            filterOption={() => true} //override select component filter
          />
          <Marker
            onDragEnd={handleMarkerDrag}
            draggable
            latitude={location.lat}
            longitude={location.lon}
            color="#2552EC"
          />
        </Map>
      </Stack>
    </form>
  );
};
